import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/content";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      // Active section detection
      let current = "home";
      for (const link of NAV_LINKS) {
        const el = document.getElementById(link.id);
        if (el && el.getBoundingClientRect().top <= 140) current = link.id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const go = useCallback(
    (link) => {
      const target = typeof link === "string" ? { id: link } : link;
      setOpen(false);
      if (target.to) {
        navigate(target.to);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      const id = target.id;
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          if (id === "home") window.scrollTo({ top: 0, behavior: "smooth" });
          else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 120);
        return;
      }
      if (id === "home") window.scrollTo({ top: 0, behavior: "smooth" });
      else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    },
    [location.pathname, navigate]
  );

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-ivory/85 border-b border-champagne/25 py-3 shadow-[0_4px_30px_rgba(184,122,91,0.07)]" : "py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
        <button onClick={() => go("home")} data-testid="nav-logo" className="flex flex-col items-start leading-none">
          <span className="font-heading text-xl md:text-2xl tracking-[0.18em] text-ink">LEAH THAMES</span>
          <span className="font-body text-[0.55rem] md:text-[0.6rem] tracking-[0.35em] uppercase text-bronze mt-0.5">Fast · Feminine · Focused</span>
        </button>

        <div className="hidden lg:flex items-center gap-5">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l)}
              data-testid={`nav-link-${l.id}`}
              className={`font-body text-[0.72rem] tracking-wide uppercase transition-colors ${
                (l.to ? location.pathname === l.to : active === l.id) ? "text-bronze" : "text-ink/70 hover:text-bronze"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => go("partnerships")}
            data-testid="nav-cta-partner"
            className="rounded-full bg-blush px-6 py-2.5 font-body text-[0.78rem] font-semibold text-ink hover:bg-bronze hover:text-ivory transition-all"
          >
            Partner With Leah
          </button>
        </div>

        <button data-testid="nav-mobile-toggle" className="lg:hidden text-ink p-2" onClick={() => setOpen((o) => !o)} aria-label="Toggle menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden mt-3 mx-4 rounded-3xl bg-ivory/97 backdrop-blur-xl border border-champagne/30 p-6 shadow-xl">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l)}
                data-testid={`nav-mobile-${l.id}`}
                className="text-left font-body text-sm uppercase tracking-wide text-ink/80 hover:text-bronze py-3 border-b border-champagne/15 last:border-0"
              >
                {l.label}
              </button>
            ))}
            <button onClick={() => go("partnerships")} className="mt-4 text-center rounded-full bg-blush px-6 py-3 font-body text-sm font-semibold text-ink">
              Partner With Leah
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
